import { centsToAudDisplay } from './money.js';
import { bpToPercentDisplay } from './percent.js';

/**
 * Live search match for the pricing list: case-insensitive substring against the
 * course name OR the college/provider name. An empty query matches everything.
 */
export function matchesCourseQuery(
  row: { name: string; providerName: string },
  query: string,
): boolean {
  const q = query.trim().toLowerCase();
  if (q === '') return true;
  return row.name.toLowerCase().includes(q) || row.providerName.toLowerCase().includes(q);
}

/**
 * The plain-language "are you sure?" line shown before a save, stating the change.
 * "Set fee for Diploma of IT to $46,964.50? Current: $42,000.00"
 * "Set fee for Diploma of IT to $46,964.50? Current: Not set"
 */
export function buildFeeConfirm(
  courseName: string,
  newCents: number,
  currentCents: number | null,
): string {
  const current = currentCents == null ? 'Not set' : centsToAudDisplay(currentCents);
  return `Set fee for ${courseName} to ${centsToAudDisplay(newCents)}? Current: ${current}`;
}

/**
 * A scholarship version as the web receives it (list row or history row).
 * `kind` is loose (string) so any row-shaped object fits without casts.
 */
export type ScholarshipLike = {
  kind: string; // 'percent' | 'amount' | 'none' from the API
  percentBp: number | null;
  amountCents: number | null;
};

/** A scholarship actually in force — kind 'none' (removed) has no active value. */
export type ActiveScholarship =
  | { kind: 'percent'; percentBp: number }
  | { kind: 'amount'; amountCents: number };

/**
 * Normalise the API's scholarship object to the value in force. `null` /
 * `undefined` (never set), kind 'none' (removed) and malformed rows (a kind
 * with no matching value) all mean: no scholarship in force.
 */
export function activeScholarship(s: ScholarshipLike | null | undefined): ActiveScholarship | null {
  if (!s) return null;
  if (s.kind === 'percent' && s.percentBp != null) {
    return { kind: 'percent', percentBp: s.percentBp };
  }
  if (s.kind === 'amount' && s.amountCents != null) {
    return { kind: 'amount', amountCents: s.amountCents };
  }
  return null;
}

/** "10.5%" for a percent scholarship, "$2,000.00" for an amount one. */
export function scholarshipLabel(v: ActiveScholarship): string {
  return v.kind === 'percent' ? bpToPercentDisplay(v.percentBp) : centsToAudDisplay(v.amountCents);
}

/**
 * History-row label. A kind-'none' version is the removal event itself, so it
 * reads "Removed"; a malformed row renders the honest em dash.
 */
export function scholarshipVersionLabel(v: ScholarshipLike): string {
  if (v.kind === 'none') return 'Removed';
  const active = activeScholarship(v);
  return active ? scholarshipLabel(active) : '—';
}

/**
 * True when the server-computed net fee is negative — an amount scholarship
 * now exceeds a later-lowered fee. The value is shown honestly, never clamped.
 */
export function isNegativeNet(netFeeCents: number | null | undefined): boolean {
  return netFeeCents != null && netFeeCents < 0;
}

/**
 * The plain-language "are you sure?" line for a scholarship save, sibling of
 * buildFeeConfirm. A current version of kind 'none' reads like no scholarship
 * (pass it through activeScholarship), so a set-after-removal is a fresh "Set".
 *
 * "Set a 10% scholarship?" · "Change the scholarship from 10% to $2,000.00?"
 * "Remove the 10% scholarship?" · degenerate: "Remove the scholarship?"
 */
export function buildScholarshipConfirm(
  current: ActiveScholarship | null,
  next: ActiveScholarship | { kind: 'none' },
): string {
  if (next.kind === 'none') {
    return current
      ? `Remove the ${scholarshipLabel(current)} scholarship?`
      : 'Remove the scholarship?';
  }
  return current
    ? `Change the scholarship from ${scholarshipLabel(current)} to ${scholarshipLabel(next)}?`
    : `Set a ${scholarshipLabel(next)} scholarship?`;
}
